import { IconButton } from '@mui/material';
import Tooltip from '@mui/material/Tooltip';
import { memo } from 'react';
import { Link } from 'react-router-dom';

import { ServiceProtocolDto } from 'api';
import { CheckmarkCircle, Remove } from 'assets/volvoIcons/Control';
import { ServiceProtocolStatus } from 'domain/serviceProtocol/ServiceProtocolDto';
import {
	getLinkOpenInitiatedServiceProtocol,
	getLinkOpenServiceProtocolView,
} from 'library/components/link/linkUtils';
import { useAdobeTracking } from 'library/hooks/useAdobeTracking';

const ServiceProtocolStatusCellInternal = ({
	id,
	status,
	drivelineId,
	trackingSource,
}: Pick<ServiceProtocolDto, 'id' | 'status'> & {
	drivelineId: number;
	/** When provided, tracks click with this source identifier */
	trackingSource?: string;
}): React.JSX.Element => {
	const { trackApplicationInteraction } = useAdobeTracking();
	const isInitiated = status === 'Initiated';

	const serviceProtocolPath = isInitiated
		? getLinkOpenInitiatedServiceProtocol(id, drivelineId)
		: getLinkOpenServiceProtocolView(id);

	const handleClick = () => {
		void trackApplicationInteraction({
			action: 'click',
			stepName: trackingSource
				? `${trackingSource} Service Protocol Link`
				: 'Service Protocol Link',
			selectedOptions: [
				{ attribute: 'protocolId', value: id.toString() },
				{ attribute: 'status', value: status },
				{ attribute: 'drivelineId', value: drivelineId.toString() },
			],
		});
	};

	return (
		<div>
			<IconButton
				component={Link}
				target={'_self'}
				to={serviceProtocolPath}
				onClick={handleClick}>
				<ServiceProtocolSymbol status={status} />
			</IconButton>
		</div>
	);
};

export const ServiceProtocolStatusCell = memo(
	ServiceProtocolStatusCellInternal
);

export const ServiceProtocolSymbol = ({
	status,
}: {
	status: ServiceProtocolStatus;
}): React.JSX.Element | null => {
	switch (status) {
		case 'Cancelled':
			return (
				<Tooltip title={'Cancelled'}>
					<span>
						<Remove color="disabled" />
					</span>
				</Tooltip>
			);
		case 'Finalized':
			return (
				<Tooltip title={'Finalized'}>
					<span>
						<CheckmarkCircle color="success" />
					</span>
				</Tooltip>
			);
		case 'Initiated':
			return (
				<Tooltip title={'Initiated'}>
					<span>
						<CheckmarkCircle color="warning" />
					</span>
				</Tooltip>
			);
		default:
			return null;
	}
};
